import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaRobot } from 'react-icons/fa';
import { Popover } from 'react-tiny-popover';
import {
  useAiMetadataApi,
  Agent,
  Team,
  Workflow,
} from '@/features/ai-agents/shared/use-ai-metadata-api';
import { SelectedAgent } from '@/features/ai-agents/shared/models';
import { getQueryParam } from '@/utils/browser';
import { Flexbox } from '@/components/flex';

export interface AgentSelectionDropdownProps {
  onAgentSelect?: (agent: SelectedAgent) => void;
  selectedAgent?: SelectedAgent;
}

export const AgentSelectionDropdown: React.FC<AgentSelectionDropdownProps> = ({
  onAgentSelect,
  selectedAgent,
}) => {
  const [isAgentPopoverOpen, setIsAgentPopoverOpen] = useState(false);
  const { listAll, loading: metadataLoading } = useAiMetadataApi(
    getQueryParam(`local-ai`) === `1`,
  );
  const [resources, setResources] = useState<{
    agents: Agent[];
    teams: Team[];
    workflows: Workflow[];
  }>({ agents: [], teams: [], workflows: [] });

  useEffect(() => {
    listAll().then((data) => {
      if (data) {
        setResources(data);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAgentSelection = useCallback(
    (type: 'agent' | 'team' | 'workflow', id: string) => {
      if (onAgentSelect) {
        onAgentSelect({ type, id });
      }
      setIsAgentPopoverOpen(false);
    },
    [onAgentSelect],
  );

  const filteredAgents = React.useMemo(() => {
    if (
      !selectedAgent ||
      selectedAgent.type !== `agent` ||
      !resources.agents.length
    ) {
      return resources.agents;
    }

    const selectedAgentId = selectedAgent.id;
    const selectedWords = selectedAgentId
      .split(`-`)
      .filter((word) => word.length > 0);

    return resources.agents.filter((agent) => {
      const agentId = agent.agent_id || agent.id || ``;
      const agentWords = agentId.split(`-`).filter((word) => word.length > 0);

      // Count common words between the two arrays
      const commonWords = selectedWords.filter((word) =>
        agentWords.includes(word),
      );

      // Return true if there are at least 2 common words
      return commonWords.length >= 2;
    });
  }, [resources.agents, selectedAgent]);

  return (
    <Popover
      isOpen={isAgentPopoverOpen}
      positions={[`top`, `bottom`, `left`, `right`]}
      onClickOutside={() => setIsAgentPopoverOpen(false)}
      content={
        <AgentDropdown>
          {metadataLoading && (
            <LoadingItem role="status" aria-live="polite">
              Loading...
            </LoadingItem>
          )}
          {!metadataLoading && (
            <div>
              {filteredAgents.length > 0 && (
                <>
                  {filteredAgents.map((agent) => (
                    <DropdownItem
                      key={agent.agent_id || agent.id}
                      onClick={() =>
                        handleAgentSelection(
                          `agent`,
                          agent.agent_id || agent.id || ``,
                        )
                      }
                    >
                      {agent.agent_id || agent.id}
                    </DropdownItem>
                  ))}
                </>
              )}
            </div>
          )}
        </AgentDropdown>
      }
    >
      <Flexbox align="center" justify="center" direction="row" gap="0.5rem">
        <FaRobot
          size={20}
          onClick={() => setIsAgentPopoverOpen((prev) => !prev)}
          role="button"
          aria-label="Select agent"
          aria-expanded={isAgentPopoverOpen}
          tabIndex={0}
          style={{ cursor: `pointer` }}
        />
        {selectedAgent && <AgentLabel>{selectedAgent.id}</AgentLabel>}
      </Flexbox>
    </Popover>
  );
};

const AgentDropdown = styled.div`
  background: white;
  border-radius: 0.8rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: solid 1px #e7eaed;
  padding: 0.8rem 0;
  min-width: 20rem;
  max-height: 30rem;
  overflow-y: auto;
  z-index: 1000;
`;

const DropdownItem = styled.div`
  font-family: Eesti, sans-serif;
  font-size: 1.4rem;
  padding: 1rem 1.6rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f6f5f4;
  }
`;

const LoadingItem = styled.div`
  font-family: Eesti, sans-serif;
  font-size: 1.4rem;
  padding: 1rem 1.6rem;
  color: #999;
`;

const AgentLabel = styled.span`
  font-family: Eesti, sans-serif;
  font-size: 1.2rem;
  color: #666;
  margin-right: 0.4rem;
  white-space: nowrap;
`;
